(() => {
  if (window.mmsCwtShippingProgress) return;
  const elements = () => Array.from(document.querySelectorAll('mms-cwt-shipping-progress'));
  let state = null;
  let timer;
  let pending = false;
  let again = false;
  let revision = 0;
  const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(cents / 100);
  const cartEstimate = (cart, market, profiles) => {
    const fallback = { feeCents: null, showProgress: false, message: 'Shipping calculated at checkout.', estimate: 'Your delivery address and cart determine the final rate.' };
    if (!Array.isArray(cart.items)) return fallback;
    if (!cart.items.length) return { feeCents: null, showProgress: market === 'US' && cart.currency === 'USD', message: market === 'US' && cart.currency === 'USD' ? 'Add $150 for free US shipping' : 'Your cart is empty.', estimate: '' };
    if (market !== 'US' || cart.currency !== 'USD') return fallback;
    if (cart.items.some(item => item.selling_plan_allocation)) return fallback;
    const expanded = cart.items.flatMap(item => {
      if (Array.isArray(item.item_components) && item.item_components.length) return item.item_components;
      // Exact native bundle mappings only; never trust editable line properties.
      const index = profiles?.nativeBundles?.[String(item.variant_id)];
      const components = Number.isInteger(index) ? profiles?.bundleComponentIndexes?.[index] : null;
      if (Array.isArray(components) && components.length) return components.map(componentIndex => ({ variant_id: profiles.standard?.[componentIndex], requires_shipping: true }));
      return [item];
    });
    const physical = expanded.filter(item => item.requires_shipping !== false);
    if (!physical.length) return { feeCents: 0, showProgress: false, message: 'No shipping required for these items.', estimate: '' };
    if (physical.some(item => item.selling_plan_allocation)) return fallback;
    if (!profiles) return fallback;
    const standard = new Set(profiles.standard.map(String));
    const sample = new Set(profiles.sample.map(String));
    let hasStandard = false;
    let hasSample = false;
    for (const item of physical) {
      const id = String(item.variant_id);
      if (standard.has(id)) hasStandard = true;
      else if (sample.has(id)) hasSample = true;
      else return fallback;
    }
    const total = Math.max(0, Number(cart.total_price) || 0);
    const fee = (hasStandard && total < 15000 ? 995 : 0) + (hasSample && total < 1000 ? 1000 : 0);
    const remaining = Math.max(0, 15000 - total);
    return {
      feeCents: fee,
      showProgress: hasStandard,
      message: hasStandard ? (remaining ? `Add ${money(remaining)} for free US shipping` : 'Free US shipping offer unlocked') : (fee ? 'Scent Sample Pack shipping estimate' : 'Free US shipping for your sample pack'),
      estimate: `Estimated US shipping: ${fee ? money(fee) : 'Free'}. Final rate confirmed at checkout.`
    };
  };
  const render = () => {
    if (!state) return;
    for (const node of elements()) {
      const total = Math.max(0, Number(state.total_price) || 0);
      let profiles = null;
      try { profiles = JSON.parse(document.querySelector('[data-mms-shipping-profiles]')?.textContent || 'null'); } catch (_) {}
      const result = cartEstimate(state, node.dataset.market, profiles);
      const message = result.message;
      const showProgress = result.showProgress;
      if (node.dataset.cartContext === 'true') {
        const estimate = node.querySelector('.mms-shipping-progress__estimate');
        if (estimate && estimate.textContent !== result.estimate) estimate.textContent = result.estimate;
        const summary = result.estimate.startsWith('Estimated US shipping:') ? result.estimate : result.message;
        document.querySelectorAll('[data-mms-cart-shipping-estimate]').forEach(line => {
          const value = Array.isArray(state.items) && !state.items.length ? '' : summary;
          if (line.textContent !== value) line.textContent = value;
        });
        document.querySelectorAll('[data-mms-cart-inclusive-total]').forEach(row => {
          const known = Number.isInteger(result.feeCents) && Array.isArray(state.items) && state.items.length > 0;
          row.hidden = !known;
          const value = row.querySelector('[data-mms-estimated-value]');
          const label = row.querySelector('[data-mms-estimated-label]');
          if (known) {
            const formatted = `${money(total + result.feeCents)} USD`;
            if (value.textContent !== formatted) value.textContent = formatted;
            const taxesIncluded = typeof state.taxes_included === 'boolean' ? state.taxes_included : row.dataset.taxesIncluded === 'true';
            const title = taxesIncluded ? 'Estimated total' : 'Estimated total before tax';
            if (label.textContent !== title) label.textContent = title;
          } else if (value.textContent) value.textContent = '';
        });
      }
      const text = node.querySelector('.mms-shipping-progress__message');
      if (text && text.textContent !== message) text.textContent = message;
      const track = node.querySelector('[role="progressbar"]');
      if (track) {
        track.hidden = !showProgress;
        track.setAttribute('aria-valuenow', String(Math.min(15000, total) / 100));
        track.setAttribute('aria-valuetext', message);
        track.firstElementChild.style.width = `${Math.min(100, total / 150)}%`;
      }
    }
  };
  const refresh = async () => {
    if (pending) { again = true; return; }
    const node = elements()[0];
    if (!node) return;
    pending = true;
    const requestRevision = revision;
    try {
      const cartUrl = `${node.dataset.cartUrl}${node.dataset.cartUrl.includes('?') ? '&' : '?'}_mms_shipping=${Date.now()}`;
      const response = await fetch(cartUrl, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (typeof cart.total_price !== 'number' || typeof cart.currency !== 'string') throw new Error('Invalid cart');
      if (requestRevision === revision) { state = cart; render(); }
    } catch (_) {
      if (requestRevision !== revision) return;
      for (const el of elements()) {
        el.querySelector('.mms-shipping-progress__message').textContent = 'Shipping calculated at checkout.';
        const estimate = el.querySelector('.mms-shipping-progress__estimate');
        if (estimate) estimate.textContent = 'Check your cart for the latest total.';
        document.querySelectorAll('[data-mms-cart-shipping-estimate]').forEach(line => { line.textContent = 'Shipping calculated at checkout.'; });
        document.querySelectorAll('[data-mms-cart-inclusive-total]').forEach(row => { row.hidden = true; row.querySelector('[data-mms-estimated-value]').textContent = ''; });
        el.querySelector('[role="progressbar"]').hidden = true;
      }
    } finally {
      pending = false;
      if (again) { again = false; schedule(); }
    }
  };
  const schedule = () => {
    if (pending) { again = true; return; }
    if (timer) return;
    timer = setTimeout(() => { timer = null; refresh(); }, 100);
  };
  const invalidateAndRefresh = () => { revision += 1; schedule(); };
  window.mmsCwtShippingProgress = { refresh: schedule };
  const start = () => {
    const headerMeter = document.querySelector('.announcement-bar-section mms-cwt-shipping-progress:not(.mms-shipping-progress--compact)');
    if (headerMeter) {
      const section = headerMeter.closest('.announcement-bar-section');
      const measure = () => document.documentElement.style.setProperty('--mms-cwt-shipping-height', `${Math.ceil(section.getBoundingClientRect().height)}px`);
      measure();
      if (typeof ResizeObserver !== 'undefined') new ResizeObserver(measure).observe(section);
      window.addEventListener('resize', measure);
      window.addEventListener('pageshow', measure);
    }
    const first = elements()[0];
    if (first) { state = { total_price: Number(first.dataset.total), currency: first.dataset.currency, items: Number(first.dataset.cartCount) === 0 ? [] : undefined }; render(); }
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      const cart = event?.cartData;
      if (cart && typeof cart.total_price === 'number' && Array.isArray(cart.items)) {
        revision += 1;
        state = { ...cart, currency: cart.currency || state?.currency || elements()[0]?.dataset.currency };
        render();
      } else invalidateAndRefresh();
    });
    for (const event of ['cart:updated', 'cart:change', 'cart:refresh']) document.addEventListener(event, invalidateAndRefresh);
    document.addEventListener('shopify:section:load', schedule);
    window.addEventListener('pageshow', event => { if (event.persisted) invalidateAndRefresh(); else schedule(); });
    window.addEventListener('focus', invalidateAndRefresh);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) invalidateAndRefresh(); });
    const observer = new MutationObserver(records => {
      const changed = records.some(record => {
        const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
        if (target?.closest?.('[data-mms-cart-shipping-estimate], [data-mms-cart-inclusive-total]')) return false;
        return target?.closest?.('cart-drawer, cart-notification, cart-items, #cart-icon-bubble, #main-cart-footer') || Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches?.('mms-cwt-shipping-progress, cart-drawer, cart-notification, cart-items, #cart-icon-bubble') || node.querySelector?.('mms-cwt-shipping-progress')));
      });
      if (changed) schedule();
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    schedule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
